import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  PencilIcon,
  CalendarIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import jobService from '../../services/jobService';

/**
 * StepReview Component
 * Third step of job creation wizard - review and confirm all information
 */
export function StepReview({ formData, onBack, onSubmit, isSubmitting }) {
  const [displayData, setDisplayData] = useState({
    hospital: null,
    location: null,
    products: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load full details for display
  useEffect(() => {
    const loadDisplayData = async () => {
      setIsLoading(true);

      try {
        const [hospitalsData, locationsData, productsData] = await Promise.all([
          jobService.getHospitals(),
          formData.hospitalId ? jobService.getLocations(formData.hospitalId) : Promise.resolve([]),
          jobService.getProducts(),
        ]);

        const hospital = hospitalsData.find((h) => h.id === formData.hospitalId);
        const location = locationsData.find((l) => l.id === formData.locationId);
        const products = productsData.filter((p) => formData.productIds.includes(p.id));

        setDisplayData({
          hospital,
          location,
          products,
        });
      } catch (error) {
        console.error('Failed to load display data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDisplayData();
  }, [formData]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-green-900">
              Informações preenchidas com sucesso!
            </p>
            <p className="text-xs text-green-700 mt-1">
              Revise os dados abaixo antes de criar o job.
            </p>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Informações Básicas</h3>
        </div>

        <div className="p-6 space-y-4">
          {/* Hospital */}
          <div className="flex items-start gap-3">
            <BuildingOffice2Icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Hospital
              </p>
              <p className="text-sm font-medium text-gray-900">
                {displayData.hospital?.nome || '-'}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                Código: {displayData.hospital?.codigo || '-'}
              </p>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-start gap-3">
            <MapPinIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Local
              </p>
              <p className="text-sm font-medium text-gray-900">
                {displayData.location?.nome || '-'}
              </p>
            </div>
          </div>

          {/* Title */}
          <div className="pt-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Título do Job
            </p>
            <p className="text-sm font-medium text-gray-900">{formData.titulo || '-'}</p>
          </div>

          {/* Description */}
          {formData.descricao && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Descrição
              </p>
              <p className="text-sm text-gray-700">{formData.descricao}</p>
            </div>
          )}

          {/* Expected Date */}
          <div className="flex items-start gap-3">
            <CalendarIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Data Prevista
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(formData.dataPrevista)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Produtos Selecionados</h3>
            <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
              {displayData.products.length} produtos
            </span>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {displayData.products.length === 0 ? (
            <div className="p-8 text-center">
              <CubeIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">Nenhum produto selecionado</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {displayData.products.map((product, index) => (
                <div
                  key={product.id}
                  className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4"
                >
                  <div className="flex-shrink-0 w-8 text-center">
                    <span className="text-xs font-bold text-gray-400">
                      {index + 1}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {product.nome}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {product.codigo}
                      </span>
                      {product.categoria && (
                        <span className="text-xs text-gray-600">
                          {product.categoria}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900">
                      R$ {product.preco_unitario.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500">{product.unidade}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => onBack()}
          className="flex-1 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all flex items-center justify-center gap-2"
        >
          <PencilIcon className="w-4 h-4" />
          Editar Produtos
        </button>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-4 border-t-2 border-gray-200">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Voltar
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-lg shadow-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-4 focus:ring-green-100 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Criando Job...
            </span>
          ) : (
            'Criar Job '
          )}
        </button>
      </div>
    </div>
  );
}
